import React, { useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { useOverlayA11y } from '../../hooks/useOverlayA11y'
import type { Provider } from '../../lib/types'

export function InviteProviderModal({ providers, onClose, onInvited }: {
  providers: Provider[]
  onClose: () => void
  onInvited: () => void
}) {
  const invitable = providers.filter((p) => !p.user_id)
  const [providerId, setProviderId] = useState(invitable[0]?.id || '')
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ accept_path: string; email: string; email_sent: boolean } | null>(null)
  const modalRef = useOverlayA11y(onClose)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const invite = await api('/provider-invites', {
        method: 'POST',
        body: JSON.stringify({ provider_id: providerId, email }),
      })
      setResult({ accept_path: invite.accept_path, email: invite.email, email_sent: invite.email_sent })
      onInvited()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send invite.')
    } finally {
      setBusy(false)
    }
  }

  const acceptUrl = result ? `${window.location.origin}${result.accept_path}` : ''

  return (
    <div className="modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal" ref={modalRef} role="dialog" aria-modal="true" aria-label="Invite provider" tabIndex={-1}>
        <h2>Invite provider</h2>

        {!result && (
          <form onSubmit={submit}>
            {invitable.length === 0 ? (
              <p className="muted" style={{ fontSize: 13.5 }}>
                Every provider already has a linked login. Add a new provider first if you need to invite
                someone else.
              </p>
            ) : (
              <>
                <div className="field">
                  <label>Provider</label>
                  <select className="inp" value={providerId} onChange={(e) => setProviderId(e.target.value)} required>
                    {invitable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Provider's email</label>
                  <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="provider@example.com" />
                </div>
                {error && <div className="form-error">{error}</div>}
                <button className="btn full" disabled={busy}>{busy ? 'Sending invite…' : 'Send invite'}</button>
              </>
            )}
          </form>
        )}

        {result && (
          <div>
            <p style={{ fontSize: 14 }}>
              {result.email_sent
                ? <>An invite email has been sent to <b>{result.email}</b>.</>
                : <>Invite created for <b>{result.email}</b>, but the invite email couldn't be sent. Copy this link and send it to them yourself:</>}
            </p>
            <div className="field">
              <label className="muted" style={{ fontSize: 12.5 }}>
                {result.email_sent ? "Or share this link directly:" : "Invite link"}
              </label>
              <input className="inp" readOnly value={acceptUrl} onFocus={(e) => e.target.select()} />
            </div>
            <p className="muted" style={{ fontSize: 12.5 }}>This link expires in 7 days and can only be used once.</p>
            <button className="btn full" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  )
}
